/**
 * Client-side route heuristics for profitability / map UI (great-circle miles).
 * Not road-network optimisation — see UI copy where surfaced.
 */

export type RouteStopForSuggestion = {
  latitude: number | null;
  longitude: number | null;
};

export type SuggestedRouteOrder = {
  /** Original stop indices (into the caller's `stops` array) in visit order */
  order: number[];
  segmentMiles: number[];
  totalMiles: number;
};

/** Haversine distance in miles between two points (lat/lon in degrees). */
export function haversineMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Suggest stop order to minimise total great-circle distance.
 * Keeps first and last **valid** stop fixed (depot-style). Middle stops use nearest-neighbour
 * seed + segment-reversal (2-opt–style) improvement.
 */
export function suggestRouteOrder(stops: RouteStopForSuggestion[]): SuggestedRouteOrder | null {
  const valid = stops
    .map((s, i) => ({ i, lat: s.latitude, lon: s.longitude }))
    .filter((p) => p.lat != null && p.lon != null && Number.isFinite(p.lat) && Number.isFinite(p.lon)) as {
    i: number;
    lat: number;
    lon: number;
  }[];
  if (valid.length < 2) return null;
  const n = valid.length;
  const first = 0;
  const last = n - 1;
  if (n === 2) {
    const mi = haversineMiles(valid[0].lat, valid[0].lon, valid[1].lat, valid[1].lon);
    return { order: [valid[0].i, valid[1].i], segmentMiles: [mi], totalMiles: mi };
  }
  const dist = (a: number, b: number) =>
    haversineMiles(valid[a].lat, valid[a].lon, valid[b].lat, valid[b].lon);
  const middle = n - 2;
  const middleIndices = Array.from({ length: middle }, (_, i) => i + 1);
  let bestOrder = [first, ...middleIndices, last];
  let bestTotal = 0;
  for (let i = 0; i < bestOrder.length - 1; i++) bestTotal += dist(bestOrder[i], bestOrder[i + 1]);
  const tryOrder = (order: number[]) => {
    let t = 0;
    for (let i = 0; i < order.length - 1; i++) t += dist(order[i], order[i + 1]);
    return t;
  };
  const unvisited = new Set(middleIndices);
  const nnOrder = [first];
  let curr = first;
  while (unvisited.size > 0) {
    let best = -1;
    let bestD = Infinity;
    for (const j of unvisited) {
      const d = dist(curr, j);
      if (d < bestD) {
        bestD = d;
        best = j;
      }
    }
    if (best === -1) break;
    nnOrder.push(best);
    unvisited.delete(best);
    curr = best;
  }
  nnOrder.push(last);
  const nnTotal = tryOrder(nnOrder);
  if (nnTotal < bestTotal) {
    bestOrder = nnOrder;
    bestTotal = nnTotal;
  }
  for (let iter = 0; iter < 50; iter++) {
    let improved = false;
    for (let i = 1; i < bestOrder.length - 2; i++) {
      for (let k = i + 1; k < bestOrder.length - 1; k++) {
        const next = [...bestOrder.slice(0, i), ...bestOrder.slice(i, k + 1).reverse(), ...bestOrder.slice(k + 1)];
        const nextTotal = tryOrder(next);
        if (nextTotal < bestTotal) {
          bestOrder = next;
          bestTotal = nextTotal;
          improved = true;
        }
      }
    }
    if (!improved) break;
  }
  const segmentMiles: number[] = [];
  for (let i = 0; i < bestOrder.length - 1; i++) {
    segmentMiles.push(dist(bestOrder[i], bestOrder[i + 1]));
  }
  const origIndices = bestOrder.map((p) => valid[p].i);
  return { order: origIndices, segmentMiles, totalMiles: bestTotal };
}
